/**
 * Phase 2 Communications — read the LogicalCall + transcript for a call bubble.
 *
 * Lightweight per-bubble hook: fetches `/api/calls/{lcid}` once (call
 * metadata + transcript + participants) and lazily fetches
 * `/api/calls/{lcid}/recording` (signed audio URL) only when the user
 * explicitly requests playback.
 *
 * All returned strings are user-facing — no provider terminology.
 */
import { useCallback, useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';

const API = process.env.REACT_APP_BACKEND_URL || '';

const _authHeaders = async () => {
  const t = await getAuth().currentUser?.getIdToken();
  if (!t) throw new Error('not signed in');
  return { Authorization: `Bearer ${t}` };
};

export default function useLogicalCall({ logicalCallId, companyId, enabled = true }) {
  const [call, setCall]                 = useState(null);
  const [transcript, setTranscript]     = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);

  // Recording playback (lazy — fetched only on demand)
  const [audioUrl, setAudioUrl]     = useState('');
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError]     = useState(null);

  const fetchCall = useCallback(async () => {
    if (!enabled || !logicalCallId || !companyId) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await _authHeaders();
      const res = await fetch(
        `${API}/api/calls/${encodeURIComponent(logicalCallId)}?companyId=${encodeURIComponent(companyId)}`,
        { headers },
      );
      if (res.status === 404) { setError('not_found'); return; }
      if (res.status === 403) { setError('forbidden'); return; }
      if (!res.ok)            { setError('server');    return; }
      const body = await res.json();
      setCall(body.call || null);
      setTranscript(body.transcript || null);
      setParticipants(body.participants || []);
    } catch (_err) {
      setError('network');
    } finally {
      setLoading(false);
    }
  }, [enabled, logicalCallId, companyId]);

  useEffect(() => { fetchCall(); }, [fetchCall]);

  const loadAudio = useCallback(async () => {
    if (audioUrl || audioLoading) return audioUrl;
    if (!logicalCallId || !companyId) return '';
    setAudioLoading(true);
    setAudioError(null);
    try {
      const headers = await _authHeaders();
      const res = await fetch(
        `${API}/api/calls/${encodeURIComponent(logicalCallId)}/recording?companyId=${encodeURIComponent(companyId)}`,
        { headers },
      );
      if (res.status === 404) { setAudioError('unavailable'); return ''; }
      if (!res.ok)            { setAudioError('server');      return ''; }
      const body = await res.json();
      const url = body.signedUrl || '';
      setAudioUrl(url);
      return url;
    } catch (_err) {
      setAudioError('network');
      return '';
    } finally {
      setAudioLoading(false);
    }
  }, [logicalCallId, companyId, audioUrl, audioLoading]);

  const refresh = useCallback(() => fetchCall(), [fetchCall]);

  return {
    call, transcript, participants,
    loading, error, refresh,
    audioUrl, audioLoading, audioError, loadAudio,
  };
}
